import "./style/Coaches.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCreditLedger } from "../apicalls/credits";
import { isAuthenticated } from "../apicalls/auth";

const normalizeLedgerRows = (data) => {
  if (Array.isArray(data?.entries)) return data.entries;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const defaultPagination = {
  page: 1,
  perPage: 50,
  total: 0,
  totalPages: 1,
  sortBy: "created_at",
  sortDir: "desc",
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const columns = [
  { key: "id", label: "ID", sortable: true },
  { key: "player_full_name", label: "Player Name", sortable: true },
  { key: "player_email", label: "Player Email", sortable: true },
  { key: "amount", label: "Amount", sortable: true },
  { key: "entry_type", label: "Entry Type", sortable: true },
  { key: "ref_type", label: "Ref Type", sortable: true },
  { key: "ref_id", label: "Ref ID", sortable: true },
  { key: "coach_full_name", label: "Coach Name", sortable: true },
  { key: "coach_email", label: "Coach Email", sortable: true },
  { key: "coach_id", label: "Coach ID" },
  { key: "coach_package_id", label: "Coach Package ID" },
  { key: "idempotency_key", label: "Idempotency Key" },
  { key: "meta", label: "Meta" },
  { key: "created_at", label: "Created At", sortable: true },
  { key: "updated_at", label: "Updated At", sortable: true },
];

const entryTypes = ["EARN", "HOLD", "RELEASE", "CONSUME", "EXPIRE", "REFUND"];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getEntryTypeClass = (value) => {
  if (!entryTypes.includes(value)) return "ledger-badge ledger-unknown";
  return `ledger-badge ledger-${value.toLowerCase()}`;
};

function CreditLedger() {
  const user = isAuthenticated();
  const token = user?.access_token;
  const [ledger, setLedger] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    entryType: "",
    refType: "",
    from: "",
    to: "",
  });
  const [pagination, setPagination] = useState(defaultPagination);

  const params = useMemo(() => ({
    ...filters,
    page: pagination.page,
    perPage: pagination.perPage,
    sortBy: pagination.sortBy,
    sortDir: pagination.sortDir,
  }), [filters, pagination.page, pagination.perPage, pagination.sortBy, pagination.sortDir]);

  const loadLedger = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await getCreditLedger(token, params);
      setRawResponse(data);
      setLedger(normalizeLedgerRows(data));
      setPagination((prev) => ({
        ...prev,
        ...(data.pagination || {}),
      }));
    } catch (err) {
      setError(err.message || "Failed to load credit ledger");
      setLedger([]);
    } finally {
      setLoading(false);
    }
  }, [params, token]);

  useEffect(() => {
    const timer = setTimeout(loadLedger, 250);
    return () => clearTimeout(timer);
  }, [loadLedger]);

  const rows = useMemo(() => ledger, [ledger]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const changePage = (nextPage) => {
    setPagination((prev) => ({
      ...prev,
      page: Math.min(Math.max(nextPage, 1), prev.totalPages || 1),
    }));
  };

  const changePerPage = (value) => {
    setPagination((prev) => ({
      ...prev,
      page: 1,
      perPage: Number(value),
    }));
  };

  const sortColumn = (column) => {
    if (!column.sortable) return;
    setPagination((prev) => ({
      ...prev,
      page: 1,
      sortBy: column.key,
      sortDir: prev.sortBy === column.key && prev.sortDir === "asc" ? "desc" : "asc",
    }));
  };

  const sortMark = (column) => {
    if (!column.sortable) return "";
    if (pagination.sortBy !== column.key) return " -";
    return pagination.sortDir === "asc" ? " ASC" : " DESC";
  };

  return (
    <main>
      <div className="coaches_title">
        <h1>Credit Ledger</h1>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title">Entry type guide</h6>
          <ul className="mb-3">
            <li><strong>EARN:</strong> Credits added after a package is charged. Reserved packages create no earn row until first confirmation.</li>
            <li><strong>HOLD:</strong> Credits temporarily reserved for a booking.</li>
            <li><strong>RELEASE:</strong> A prior hold was released.</li>
            <li><strong>CONSUME:</strong> Credits permanently used for a confirmed lesson.</li>
            <li><strong>EXPIRE:</strong> Credits removed because the package expired.</li>
            <li><strong>REFUND:</strong> Credits removed when a package payment is refunded.</li>
          </ul>
          <p className="mb-1"><strong>Rule of thumb for admins</strong></p>
          <ul className="mb-0">
            <li><code>Available = Earned - Refunded - Consumed - Expired - (Held - Released - Consumed)</code></li>
            <li><code>Held = Hold - Released - Consumed</code></li>
            <li>Reserved unpaid packages are not missing from the ledger; they become ledger credits only after the first-lesson charge succeeds.</li>
          </ul>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3">
              <input
                className="form-control"
                type="text"
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
                placeholder="Search names, emails, refs"
              />
            </div>
            <div className="col-md-2">
              <select className="form-control" value={filters.entryType} onChange={(e) => updateFilter("entryType", e.target.value)}>
                <option value="">All entry types</option>
                {entryTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                type="text"
                value={filters.refType}
                onChange={(e) => updateFilter("refType", e.target.value)}
                placeholder="Ref type"
              />
            </div>
            <div className="col-md-2">
              <input className="form-control" type="date" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)} />
            </div>
            <div className="col-md-2">
              <input className="form-control" type="date" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)} />
            </div>
            <div className="col-md-1">
              <select className="form-control" value={pagination.perPage} onChange={(e) => changePerPage(e.target.value)}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span>
            {pagination.total} entries - Page {pagination.page} of {pagination.totalPages}
          </span>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => changePage(pagination.page - 1)} disabled={loading || pagination.page <= 1}>
              Previous
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => changePage(pagination.page + 1)} disabled={loading || pagination.page >= pagination.totalPages}>
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div>Loading credit ledger...</div>
        ) : rows.length ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th scope="col" key={column.key}>
                      {column.sortable ? (
                        <button type="button" className="btn btn-link p-0 font-weight-bold" onClick={() => sortColumn(column)}>
                          {column.label}{sortMark(column)}
                        </button>
                      ) : column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {columns.map((column) => (
                      <td key={`${row.id ?? index}-${column.key}`}>
                        {column.key === "entry_type" ? (
                          <span className={getEntryTypeClass(row.entry_type)}>
                            {formatCellValue(row.entry_type)}
                          </span>
                        ) : column.key === "created_at" || column.key === "updated_at" ? (
                          formatDateTime(row[column.key])
                        ) : (
                          formatCellValue(row[column.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <h2>Credit ledger not available</h2>
            {rawResponse ? <pre>{JSON.stringify(rawResponse, null, 2)}</pre> : null}
          </div>
        )}
      </div>
    </main>
  );
}

export default CreditLedger;
